import { Box, Button, Modal, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useMemo, useState } from "react";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 2,
  p: 4,
};

function ConfirmationModal({
  buttonLabel,
  loadingLabel,
  confirmLabel,
  cancelLabel,
  title,
  description,
  helperText,
  levelOrder,
  selectedQuestions,
  items,
  disabled,
  loading,
  onConfirm,
  renderTrigger,
  children,
}) {
  const [open, setOpen] = useState(false);

  const usingSelectedQuestions = !items?.length;
  const derivedItems = useMemo(() => {
    if (Array.isArray(items) && items.length) {
      return items;
    }
    const fallback = selectedQuestions?.filter(Boolean) || [];
    return fallback.map((question) => {
      const trimmedText = (question.question || "Untitled question").trim();
      const readableText =
        trimmedText.length > 80 ? `${trimmedText.slice(0, 77)}...` : trimmedText;
      const orderLabel = Number.isFinite(question.order) ? question.order : "-";
      return {
        id: question._id,
        label: `Q${orderLabel}`,
        description: readableText,
      };
    });
  }, [items, selectedQuestions]);

  const handleOpen = () => {
    if (disabled) {
      return;
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleConfirm = async () => {
    if (!onConfirm) {
      handleClose();
      return;
    }

    try {
      await onConfirm();
      handleClose();
    } catch (error) {
      console.error("Failed to confirm action:", error);
    }
  };

  const triggerButton = renderTrigger ? (
    renderTrigger({ openModal: handleOpen, disabled, loading })
  ) : (
    <Button
      variant="outlined"
      color="error"
      onClick={handleOpen}
      disabled={disabled || loading}
    >
      {loading ? loadingLabel : buttonLabel}
    </Button>
  );

  return (
    <>
      {triggerButton}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
            {title}
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            {description ||
              (levelOrder
                ? `Level ${levelOrder} has ${derivedItems.length} item${derivedItems.length === 1 ? "" : "s"} selected.`
                : "")}
          </Typography>
          {children}

          {derivedItems.length > 0 && (
            <Box component="ul" sx={{ mt: 2, pl: 3, mb: 2 }}>
              {derivedItems.map((item) => {
                if (usingSelectedQuestions) {
                  return (
                    <Typography component="li" key={item.id} sx={{ mb: 0.5 }}>
                      {`${item.label}: ${item.description}`}
                    </Typography>
                  );
                }

                if (typeof item === "string") {
                  return (
                    <Typography component="li" key={item} sx={{ mb: 0.5 }}>
                      {item}
                    </Typography>
                  );
                }

                const key = item.id || item.label || item.description;
                return (
                  <Typography component="li" key={key} sx={{ mb: 0.5 }}>
                    {item.label ? `${item.label}: ` : ""}
                    {item.description || ""}
                  </Typography>
                );
              })}
            </Box>
          )}

          {helperText && (
            <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
              {helperText}
            </Typography>
          )}

          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirm}
            >
              {confirmLabel}
            </Button>
            <Button variant="outlined" color="secondary" onClick={handleClose}>
              {cancelLabel}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

ConfirmationModal.propTypes = {
  buttonLabel: PropTypes.string,
  loadingLabel: PropTypes.string,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  helperText: PropTypes.string,
  levelOrder: PropTypes.number,
  selectedQuestions: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      order: PropTypes.number,
      question: PropTypes.string,
    })
  ),
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string,
        description: PropTypes.string,
      }),
    ])
  ),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  onConfirm: PropTypes.func,
  renderTrigger: PropTypes.func,
  children: PropTypes.node,
};

ConfirmationModal.defaultProps = {
  buttonLabel: "Archive Selected",
  loadingLabel: "Processing...",
  confirmLabel: "Confirm",
  cancelLabel: "Cancel",
  title: "Confirm Archive",
  description: "",
  helperText: "",
  levelOrder: undefined,
  selectedQuestions: [],
  items: undefined,
  disabled: false,
  loading: false,
  onConfirm: undefined,
  renderTrigger: undefined,
  children: null,
};

export default ConfirmationModal;